import React from "react";

import {
  faCheck,
  faSpinner,
  faStop,
  faWarning,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { Pill, PillVariants } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { DOCS_JOBS } from "src/constants/ExternalLinks";
import { Tooltip } from "src/design-system/Tooltip";
import { JobRunStatus, JobRunStatusType } from "src/jobs/types";
import { assertUnreachable } from "src/utils/typeUtils";

export const getStatusIcon = (status: JobRunStatus) => {
  switch (status.type) {
    case JobRunStatusType.Completed:
      return faCheck;
    case JobRunStatusType.Running:
      return faSpinner;
    case JobRunStatusType.Failed:
      return faWarning;
    case JobRunStatusType.Stopped:
      return faStop;
    case JobRunStatusType.Writing:
      return faSpinner;
    default:
      assertUnreachable(status);
      return faWarning;
  }
};

export const getStatusTitle = (status: JobRunStatus) => {
  if (status.type === JobRunStatusType.Failed) {
    return status.error_title;
  }

  if (status.type === JobRunStatusType.Writing) {
    return "Writing to destination...";
  }

  return capitalize(status.type);
};

export const getStatusPillVariant = (status: JobRunStatus): PillVariants => {
  switch (status.type) {
    case JobRunStatusType.Completed:
      return "green";
    case JobRunStatusType.Running:
      return "indigo-light";
    case JobRunStatusType.Failed:
      return "red";
    case JobRunStatusType.Stopped:
      return "yellow";
    case JobRunStatusType.Writing:
      return "indigo-light";
    default:
      assertUnreachable(status);
      return "gray";
  }
};

export const RunStatusPill: React.FC<{ status: JobRunStatus }> = ({
  status,
}) => {
  const statusTitle = getStatusTitle(status);
  const pill = (
    <Pill dataLoc="run-status-pill" variant={getStatusPillVariant(status)}>
      <Pill.Icon
        icon={getStatusIcon(status)}
        spin={
          status.type === JobRunStatusType.Running ||
          status.type === JobRunStatusType.Writing
        }
      />
      <Pill.Text>{statusTitle}</Pill.Text>
    </Pill>
  );

  if (status.type === JobRunStatusType.Failed) {
    return (
      <CustomPopover
        button={<span className="cursor-pointer">{pill}</span>}
        offsetX={24}
        placement="bottom-end"
      >
        <div className="relative w-96 cursor-default">
          <CustomPopover.Button className="absolute top-4 right-4">
            <Icon color="text-gray-500" icon={faXmark} size="xs" />
          </CustomPopover.Button>
          <div className="border-gray-100 border-b px-5 pt-4 pb-3">
            <h1 className="flex items-center gap-x-1 text-left font-inter-semibold-16px text-gray-800">
              <Icon color="text-red-500" icon={faWarning} size="xs" />
              {statusTitle}
            </h1>
          </div>
          <div className="px-5 pt-3 pb-5 font-inter-normal-13px text-gray-500">
            {status.error_details}
          </div>
          <div className="flex justify-end px-5 pt-3 pb-4">
            <Button
              variant="secondary"
              onClick={() => window.open(DOCS_JOBS, "_blank")}
            >
              Learn more
            </Button>
          </div>
        </div>
      </CustomPopover>
    );
  }

  if (status.type === JobRunStatusType.Stopped) {
    return (
      <Tooltip
        body={
          <p className="font-inter-normal-12px text-gray-300">
            Stopping a Job halts the initiation of new Decisions, but the
            Decision count may still increment for those already underway.
          </p>
        }
        placement="top"
        asChild
      >
        <span className="cursor-auto">{pill}</span>
      </Tooltip>
    );
  }

  return pill;
};

//Status Pill without popover for use for failed runs in SimulationsSidePane,
// use old status pill if we want to include Popover + provide links to simulation Job run docs in the future
export const FailedStatusPillNoPopover: React.FC<{ status: JobRunStatus }> = ({
  status,
}) => (
  <Pill dataLoc="run-status-pill" variant={getStatusPillVariant(status)}>
    <Pill.Icon icon={getStatusIcon(status)} />
    <Pill.Text>{getStatusTitle(status)}</Pill.Text>
  </Pill>
);
